import { FilterHandle } from '@access-erp/common';
import { MutableRefObject } from 'react';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import { ContractFilterInput } from '@/graphql/contracts/_generated_/contracts';
import {
  GetPaginatedFilteredBCContractLines,
  GetPaginatedFilteredBCContractLinesVariables,
} from '@/graphql/contracts/_generated_/getPaginatedFilteredBCContractLines.query';
import {
  GetPaginatedFilteredVariableContractLines,
  GetPaginatedFilteredVariableContractLinesVariables,
} from '@/graphql/contracts/_generated_/getPaginatedFilteredVariableContractLines.query';
import GetPaginatedFilteredBCContractLinesQuery 
  from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery 
  from '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { BillingTypeFormatted, ContractStatusFormatted } from '../columns/types';

const useGetBCDetails = () => {
  return async (contractID: string, filtersRef: MutableRefObject<FilterHandle>) => {
    const detailFilters: ContractFilterInput = filtersRef.current?.applyToDetail
      ? filtersRef.current?.mappedDetailFilters as ContractFilterInput
      : {};
    const { data: contractLines, loading: loadingContractLines } = 
      await apolloClientContracts.query<
      GetPaginatedFilteredBCContractLines,
      GetPaginatedFilteredBCContractLinesVariables
      >({
        query: GetPaginatedFilteredBCContractLinesQuery,
        variables: {
          filter: { ...detailFilters, contractId: { eq: contractID } },
          first: ALL_ITEMS,
        },
        context: { important: true },
        fetchPolicy: 'network-only',
      });

    const { data: variableContractLines, loading: loadingVariableContractLines } =
      await apolloClientContracts.query<
      GetPaginatedFilteredVariableContractLines,
      GetPaginatedFilteredVariableContractLinesVariables
      >({
        query: GetPaginatedFilteredVariableContractLinesQuery,
        variables: {
          filter: { headerPrimaryKey: { eq: contractID } },
          first: ALL_ITEMS,
        },
        context: { important: true },
        fetchPolicy: 'network-only',
      });

    const flattenedFilteredContractLines =
      contractLines.paginatedFilteredContractLinesByBillingCycles!.items!.map((line) => ({
        ...line,
        status: line!.status,
        billingType: line!.billingType,
        billingInterval: line!.billingInterval,
        stockCode: line!.stockCode,
        stockName: line!.stockName,
      }));

    const combinedContractLines = [
      ...flattenedFilteredContractLines,
      ...variableContractLines!.paginatedFilteredVariableContractLines!.items!,
    ];

    const allContractLinesFormatted = combinedContractLines.map((line) => ({
      ...line,
      status: ContractStatusFormatted[line!.status!] ?? line!.status,
      billingType: BillingTypeFormatted[line!.billingType!] ?? line!.billingType,
      stockCode: `${line!.stockCode} - ${line!.stockName}`,
    }));
    const loadingLines = loadingContractLines || loadingVariableContractLines;
    return {
      data: allContractLinesFormatted,
      loading: loadingLines,
    };
  };
};

export default useGetBCDetails;
